import {
  BackButtonCWUI,
  ButtonUI,
  CustomerCategoryTitleUI,
} from '@next-order/next-order-components';
import React, { useReducer, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updatePersonalInformationAPI } from './actions';
import { selectStoreConfig } from 'containers/Layout/selectors';
import { validateEmail } from 'helpers/validationUtils';
import { BREAKPOINT_VALUES } from 'helpers/enums';
import AnimationWrapper from 'components/AnimationWrapper';
import { allCustomersFetched } from 'containers/Home/actions';
import MobileUpdateComponent from './MobileUpdateComponent';

// Reducer function for form state management
function formReducer(state, action) {
  switch (action.type) {
    case 'UPDATE_FIELD':
      return {
        ...state,
        values: {
          ...state.values,
          [action.field]: action.value,
        },
        errors: {
          ...state.errors,
          [action.field]: '', // clear error on change
        },
        hasChanges: true,
      };
    case 'SET_ERRORS':
      return {
        ...state,
        errors: action.errors,
      };
    case 'RESET_CHANGES':
      return {
        ...state,
        hasChanges: false,
      };
    case 'RESET':
      return action.initialState;
    default:
      return state;
  }
}

export default function PersonalInformation({
  breakPoint,
  userDetails,
  currentUser,
  onBack,
}) {
  const reduxDispatch = useDispatch();
  const storeConfig = useSelector(selectStoreConfig);
  const { storeId, restaurantId } = storeConfig;

  // Initial form state
  const initialFormState = {
    values: {
      firstName: userDetails?.firstName || '',
      lastName: userDetails?.lastName || '',
      email:
        userDetails?.email && !userDetails.email.includes('.nextorder.com.au')
          ? userDetails.email
          : '',
      mobile: userDetails?.intlPhoneNumber || '',
    },
    errors: {},
    hasChanges: false,
  };

  const [state, dispatch] = useReducer(formReducer, initialFormState);
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdated, setIsUpdated] = useState(false);
  const [apiError, setApiError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showMobileUpdate, setShowMobileUpdate] = useState(false);
  const [showScrollShadow, setShowScrollShadow] = useState(false);

  // Reset updated state when changes are made
  useEffect(() => {
    if (state.hasChanges && isUpdated) {
      setIsUpdated(false);
    }
  }, [state.hasChanges, isUpdated]);

  const handleChange = (field, value) => {
    dispatch({ type: 'UPDATE_FIELD', field, value });
    // Clear API error and success message when user starts typing
    if (apiError) {
      setApiError('');
    }
    if (successMessage) {
      setSuccessMessage('');
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!state.values.firstName.trim()) {
      newErrors.firstName = 'First name is required.';
    }

    if (!state.values.lastName.trim()) {
      newErrors.lastName = 'Last name is required.';
    }

    if (!state.values.email.trim()) {
      newErrors.email = 'Email is required.';
    } else if (!validateEmail(state.values.email)) {
      newErrors.email = 'Please enter a valid email address.';
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      dispatch({ type: 'SET_ERRORS', errors });
      return;
    }

    setIsLoading(true);
    setApiError(''); // Clear previous errors
    setSuccessMessage(''); // Clear previous success message

    try {
      const updateData = {
        firstName: state.values.firstName.trim(),
        lastName: state.values.lastName.trim(),
        email: state.values.email.trim(),
        userId: userDetails.userId,
        storeId,
        restaurantId,
      };

      // Only include mobileNumber if it has changed from initial value
      const initialMobile = userDetails?.intlPhoneNumber || '';
      if (state.values.mobile.trim() !== initialMobile) {
        updateData.mobileNumber = state.values.mobile.trim();
      }

      const userResponse = await updatePersonalInformationAPI(updateData);
      reduxDispatch(
        allCustomersFetched(currentUser, userResponse.data?.user, [])
      );
      setIsUpdated(true);
      setSuccessMessage('Personal information updated successfully!');
      dispatch({ type: 'RESET_CHANGES' });
    } catch (error) {
      console.error('Error updating personal information:', error);
      setApiError(
        error.message ||
          'Failed to update personal information. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const baseClasses =
    'bg-white border border-gray-300 text-gray-900 text-base rounded-xl focus:outline-none focus:ring-0 focus:border-blue-500 block w-full px-4 py-3 shadow-sm placeholder-gray-400';

  const renderInput = (field, label, type = 'text', disabled = false) => {
    const error = state.errors[field];
    const errorClass = error
      ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
      : '';
    const disabledClass = disabled ? '!bg-gray-100 !cursor-not-allowed' : '';
    const inputClasses = `${baseClasses} ${errorClass} ${disabledClass}`;

    return (
      <div className='personal-information-input'>
        <label className='block font-normal mb-2 text-[17px] text-gray-900'>
          {label}
        </label>
        <input
          type={type}
          className={inputClasses}
          style={type === 'tel' ? { borderRadius: '12px' } : {}}
          value={state.values[field]}
          onChange={(e) => handleChange(field, e.target.value)}
          disabled={disabled}
          required={!disabled}
        />
        {error && <p className='text-red-600 text-sm mt-1'>{error}</p>}
      </div>
    );
  };

  const renderMobileInput = () => {
    const error = state.errors.mobile;
    const errorClass = error
      ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
      : '';
    const inputClasses = `${baseClasses} ${errorClass} !bg-gray-100 !cursor-not-allowed pr-20`;

    return (
      <div>
        <label className='block font-normal mb-2 text-[17px] text-gray-900'>
          Mobile
        </label>
        <div className='relative'>
          <input
            type='tel'
            className={inputClasses}
            style={{ borderRadius: '12px' }}
            value={state.values.mobile}
            disabled={true}
            readOnly
          />
          <button
            type='button'
            onClick={() => setShowMobileUpdate(true)}
            className='absolute right-3 top-1/2 transform -translate-y-1/2 text-blue-600 hover:text-blue-800 font-medium text-sm'
          >
            Update
          </button>
        </div>
        {error && <p className='text-red-600 text-sm mt-1'>{error}</p>}
      </div>
    );
  };

  const handleMobileUpdateSuccess = (newMobileNumber) => {
    // Update the mobile number in the form state
    dispatch({ type: 'UPDATE_FIELD', field: 'mobile', value: newMobileNumber });
    setShowMobileUpdate(false);
    setSuccessMessage(
      'Mobile number updated successfully! Please update your details to save changes.'
    );
  };

  const handleScroll = (e) => {
    const container = e.target;
    const { scrollTop, scrollHeight, clientHeight } = container;

    // Show shadow when there's scrollable content and user has scrolled
    const isScrollable = scrollHeight > clientHeight;
    const hasScrolled = scrollTop > 5;

    setShowScrollShadow(isScrollable && hasScrolled);
  };

  const getButtonText = () => {
    if (isLoading) return 'Updating...';
    return 'Update Details';
  };

  const isButtonDisabled = isLoading || !state.hasChanges;

  if (showMobileUpdate) {
    return (
      <AnimationWrapper
        animation='fade-in'
        style={{
          width: '100%',
          height: '100%',
        }}
      >
        <aside className='sidebar'>
          <div className='sidebar__wrapper'>
            <div className='card__about'>
              <MobileUpdateComponent
                onBack={() => setShowMobileUpdate(false)}
                onSuccess={handleMobileUpdateSuccess}
                currentMobile={state.values.mobile}
                userDetails={userDetails}
              />
            </div>
          </div>
        </aside>
      </AnimationWrapper>
    );
  }

  return (
    <AnimationWrapper
      animation='fade-in'
      style={{
        width: '100%',
        height: '100%',
      }}
    >
      <aside className='sidebar'>
        <div className='sidebar__wrapper'>
          <div className='card__about'>
            <div className='relative h-screen pt-4'>
              <BackButtonCWUI onClick={onBack} extendedClassName='ml-5' />

              <div className='relative pb-4 px-5'>
                <CustomerCategoryTitleUI extendedClassName='mt-3.25 !mb-0'>
                  Personal Information
                </CustomerCategoryTitleUI>

                {/* Shadow below title when scrolled */}
                <div
                  className={`absolute left-0 right-0 top-full h-3 pointer-events-none transition-opacity duration-200 z-10 ${
                    showScrollShadow ? 'opacity-100' : 'opacity-0'
                  }`}
                  style={{
                    background:
                      'linear-gradient(to bottom, rgba(0, 0, 0, 0.1), transparent)',
                  }}
                />
              </div>

              {/* Scrollable Form Container */}
              <div
                className='overflow-y-auto px-5 pb-30 h-[calc(100vh-300px)] md:h-[calc(100vh-100px)] mt-0'
                onScroll={handleScroll}
              >
                <form onSubmit={handleSubmit} className='space-y-6 mt-6'>
                  {renderInput('firstName', 'First Name')}
                  {renderInput('lastName', 'Last Name')}
                  {renderInput('email', 'Email', 'email')}
                  {renderMobileInput()}
                </form>
              </div>

              {/* Fixed Update Button */}
              <div
                className={`fixed ${
                  breakPoint < BREAKPOINT_VALUES.DESKTOP
                    ? 'bottom-[60px]'
                    : 'bottom-0'
                } left-0 w-full p-4 bg-white`}
              >
                {apiError && (
                  <div className='bg-red-50 border border-red-200 rounded-lg p-2 mb-3'>
                    <p className='text-red-700 text-sm text-center font-medium'>
                      {apiError}
                    </p>
                  </div>
                )}
                {successMessage && (
                  <div className='bg-green-50 border border-green-200 rounded-lg p-2 mb-3'>
                    <p className='text-green-700 text-sm text-center font-medium'>
                      {successMessage}
                    </p>
                  </div>
                )}
                <ButtonUI
                  fullWidth
                  size='xl'
                  rounded='xl'
                  variant='contained'
                  color='primary'
                  fontSize='lg'
                  extendedClassName={`text-white shadow-cw ${
                    isButtonDisabled ? 'opacity-50' : ''
                  }`}
                  onClick={handleSubmit}
                  disabled={isButtonDisabled}
                >
                  {getButtonText()}
                </ButtonUI>
              </div>
            </div>
          </div>
        </div>
      </aside>
    </AnimationWrapper>
  );
}
